import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Star, 
  Users, 
  Heart, 
  Award, 
  Target, 
  Trophy,
  MapPin,
  Calendar,
  BookOpen,
  CheckCircle,
  Plus,
  Minus,
  User,
  GraduationCap,
  Zap,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const About = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLDivElement>(null);

  // Metallic text styling
  const metallicTextStyle = {
    background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
    filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse tracking for interactive background
  useEffect(() => {
    if (isMobile) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  // Add paddle cursor effect
  useEffect(() => {
    if (isMobile) return;

    const paddle = document.createElement('div');
    paddle.id = 'cursorPaddle';
    paddle.className = 'fixed top-0 left-0 w-11 h-11 pointer-events-none z-50';
    paddle.innerHTML = `
      <svg
        viewBox="0 0 44 44"
        fill="#C7FF00"
        stroke="#000"
        stroke-width="1.5"
        class="w-full h-full drop-shadow-[0_0_2px_#C7FF00]"
      >
        <rect x="8" y="4" width="28" height="28" rx="6"></rect>
        <rect x="18" y="32" width="8" height="10" rx="2"></rect>
      </svg>
    `;
    document.body.appendChild(paddle);

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    
    let x = 0, y = 0, tx = 0, ty = 0, prevX = 0, prevY = 0, angle = 0;
    const FOLLOW = 0.35;

    const handlePointerMove = (e: PointerEvent) => {
      tx = e.clientX;
      ty = e.clientY;
      document.body.style.cursor = 'none';
      paddle.style.opacity = '1';
    };

    const handleMouseLeave = () => {
      document.body.style.cursor = 'auto';
      paddle.style.opacity = '0';
    };

    function loop() {
      x = lerp(x, tx, FOLLOW);
      y = lerp(y, ty, FOLLOW);

      const dx = x - prevX;
      const dy = y - prevY;
      prevX = x;
      prevY = y;
      if (dx * dx + dy * dy > 0.001) {
        angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      }

      paddle.style.transform = `translate(${x - 22}px, ${y - 22}px) rotate(${angle}deg)`;
      
      requestAnimationFrame(loop);
    }

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    loop();

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.body.style.cursor = 'auto';
      if (document.body.contains(paddle)) {
        document.body.removeChild(paddle);
      }
    };
  }, [isMobile]);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "What makes PBX Pickle coaching different from other programs?",
      answer: "My approach combines 20+ years of racquet sports experience with specialized knowledge in human movement sciences. I focus on adaptive techniques that work for all ability levels, emphasizing both technical skills and mental game development. Every lesson is tailored to your individual goals and physical abilities."
    },
    {
      question: "Do you offer coaching for adaptive athletes and wheelchair players?",
      answer: "Absolutely! Adaptive sports coaching is one of my core specialties. I've worked extensively with children and adults of all abilities, developing techniques and strategies that maximize performance regardless of physical limitations. My Human Movement Sciences background helps me create personalized approaches for every athlete."
    },
    {
      question: "What can I expect in my first coaching session?",
      answer: "We'll start with an assessment of your current skills, goals, and any specific needs or challenges. I'll evaluate your movement patterns, technique fundamentals, and game strategy. From there, we'll create a personalized development plan that focuses on areas where you'll see the most improvement quickly."
    },
    {
      question: "How does the PBX Pickle apparel line support adaptive athletes?",
      answer: "Our upcoming apparel line is designed with adaptive athletes as the priority, not an afterthought. Features include easy-access closures, performance materials that move with you, and cuts specifically designed for wheelchair play and various adaptive needs. We donate 10% of adaptive gear sales to wheelchair sports programs."
    },
    {
      question: "What are your coaching credentials and experience?",
      answer: "I hold a Bachelor of Science in Human Movement Sciences from Utah State University, where I was Captain of the tennis team. I led my high school team to an IHSA Illinois State Championship in 2006 and have over a decade of professional coaching experience. I've successfully managed outdoor programs for 200+ students and specialize in adaptive sports instruction."
    },
    {
      question: "Do you offer group coaching or just individual lessons?",
      answer: "I offer both individual and group coaching options. Group sessions are great for building community and competitive skills, while individual lessons allow for intensive technique development. I also run specialized clinics for adaptive athletes and inclusive programs that welcome players of all abilities."
    },
    {
      question: "How do I book a session with Coach Felix?",
      answer: "You can contact me directly through our website contact form or give me a call. I offer a consultation to discuss your goals and determine the best coaching approach for you. I'm always excited to talk with new players and help them discover their potential on the court!"
    },
    {
      question: "What is the PBX Pickle mission beyond coaching?",
      answer: "PBX Pickle is about playing beyond expectations and breaking down barriers in sport. We're building a movement that makes pickleball truly inclusive through adaptive coaching, purposefully designed apparel, and community support. Our goal is to ensure every player feels welcome, empowered, and equipped to excel."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Navigation />
      
      {/* Dynamic Hero Section */}
      <section 
        ref={heroRef}
        className="relative pt-24 pb-12 sm:pb-20 overflow-hidden"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(199, 255, 0, 0.1) 0%, transparent 50%), linear-gradient(135deg, #000000 0%, #1a1a1a 100%)`
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-lime-400/5 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-purple-600/5 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-lime-400/3 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 sm:mb-16">
            <div className="inline-block bg-lime-400/10 border border-lime-400/20 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6 sm:mb-8">
              <span className="text-lime-400 font-bold text-xs sm:text-sm uppercase tracking-wide">About PBX Pickle</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-4 sm:mb-8 tracking-tight uppercase leading-none">
              <span style={metallicTextStyle}>PLAY BEYOND</span>
              <br />
              <span className="text-lime-400">XPECTATIONS</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-4">
              At PBX Pickle, we believe pickleball is more than just a game—it's a journey of passion, precision, and pushing boundaries. 
              This belief is at the core of everything we do, from our coaching philosophy to our upcoming line of 
              <span className="text-lime-400 font-semibold"> inclusively designed apparel</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Coach Felix Introduction */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div>
              <div className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8">
                <div className="flex items-center mb-4 sm:mb-6">
                  <User className="w-6 sm:w-8 h-6 sm:h-8 text-lime-400 mr-3 sm:mr-4" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight" style={metallicTextStyle}>
                    Meet Coach Felix
                  </h2>
                </div>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6">
                  <span className="text-lime-400 font-semibold">Hey there!</span> I'm Coach Felix, and I'm thrilled you're here. 
                  My journey with racquet sports began years ago, right here in Des Plaines, Illinois. 
                  From the moment I first stepped onto a tennis court, I knew I'd found my lifelong passion.
                </p>
                <div className="bg-black/30 rounded-xl p-4 sm:p-6">
                  <p className="text-lime-400 font-medium italic text-sm sm:text-base">
                    "Seeing the pure joy on a student's face, hearing that emphatic 'YES! I did it, Coach Felix!!!' 
                    after a great session—that's what fuels me."
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Trophy className="w-5 sm:w-6 h-5 sm:h-6 text-lime-400 mr-2 sm:mr-3" />
                    <h3 className="font-bold text-base sm:text-lg">Championship Legacy</h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Led high school team to IHSA Illinois Tennis State Team Championship (2006) and served as Captain 
                    of Utah State University Tennis team with an impressive Men's Doubles record.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <GraduationCap className="w-5 sm:w-6 h-5 sm:h-6 text-lime-400 mr-2 sm:mr-3" />
                    <h3 className="font-bold text-base sm:text-lg">Expert Education</h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Bachelor of Science in Human Movement Sciences from Utah State University - 
                    deepening understanding of body mechanics, movement optimization, and adaptive techniques.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Heart className="w-5 sm:w-6 h-5 sm:h-6 text-lime-400 mr-2 sm:mr-3" />
                    <h3 className="font-bold text-base sm:text-lg">Inclusive Coaching</h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Specialized experience working with children and adults of all abilities, 
                    including adaptive athletes and special needs students - because sport is for everyone.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-black mb-4 sm:mb-6 uppercase tracking-tight" style={metallicTextStyle}>
              My Journey to the Court
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              Two decades of playing, a decade of coaching tennis, and now a new electrifying passion: pickleball
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line - Always visible, responsive positioning */}
            <div className="absolute left-8 md:left-1/2 md:transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-lime-400 to-purple-600 rounded-full"></div>

            <div className="space-y-8 sm:space-y-16">
              {/* Timeline Item 1 - Early Days */}
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 sm:gap-8">
                {/* Mobile/Desktop Content - Left Side */}
                <div className="w-full md:w-1/2 md:text-right order-2 md:order-1 pl-20 md:pl-0">
                  <div className="bg-lime-400 text-black px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold inline-block mb-3 sm:mb-4">
                    EARLY DAYS
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Finding My Passion</h3>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                    My early days were filled with dedication and teamwork in Des Plaines, Illinois. 
                    Tennis became more than a sport—it was about understanding movement, strategy, and the incredible power of sport to bring people together.
                  </p>
                </div>
                
                {/* Timeline Icon */}
                <div className="absolute left-4 md:relative md:left-auto z-10 order-1 md:order-2 md:flex-shrink-0">
                  <div className="w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 bg-lime-400 rounded-full flex items-center justify-center shadow-lg">
                    <MapPin className="w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 text-black" />
                  </div>
                </div>
                
                {/* Content Card - Right Side */}
                <div className="w-full md:w-1/2 order-3 pl-20 md:pl-0">
                  <div className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-2xl p-4 sm:p-6 md:p-8">
                    <div className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-4 text-center">🎾</div>
                    <p className="text-lime-400 font-semibold text-center text-sm sm:text-base">Des Plaines, Illinois 60016</p>
                    <p className="text-gray-300 text-xs sm:text-sm text-center">Where the passion began</p>
                  </div>
                </div>
              </div>

              {/* Timeline Item 2 - Competition Success */}
              <div className="relative flex flex-col md:flex-row-reverse items-start md:items-center gap-6 sm:gap-8">
                {/* Mobile/Desktop Content - Right Side */}
                <div className="w-full md:w-1/2 md:text-left order-2 md:order-1 pl-20 md:pl-0">
                  <div className="bg-lime-400 text-black px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold inline-block mb-3 sm:mb-4">
                    2006 - CHAMPIONSHIP
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">State Champions</h3>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                    Leading my high school team to the IHSA Illinois Tennis State Team Championship wasn't just about winning—
                    it was about understanding leadership, teamwork, and what it takes to elevate everyone's game.
                  </p>
                </div>
                
                {/* Timeline Icon */}
                <div className="absolute left-4 md:relative md:left-auto z-10 order-1 md:order-2 md:flex-shrink-0">
                  <div className="w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <Trophy className="w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 text-white" />
                  </div>
                </div>
                
                {/* Content Card - Left Side */}
                <div className="w-full md:w-1/2 order-3 pl-20 md:pl-0">
                  <div className="bg-gradient-to-br from-purple-600/10 to-lime-400/10 border border-purple-600/20 rounded-2xl p-4 sm:p-6 md:p-8">
                    <div className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-4 text-center">🏆</div>
                    <p className="text-purple-400 font-semibold text-center text-sm sm:text-base">IHSA State Champions</p>
                    <p className="text-gray-300 text-xs sm:text-sm text-center">High School Tennis Team</p>
                  </div>
                </div>
              </div>

              {/* Timeline Item 3 - College & Education */}
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 sm:gap-8">
                {/* Mobile/Desktop Content - Left Side */}
                <div className="w-full md:w-1/2 md:text-right order-2 md:order-1 pl-20 md:pl-0">
                  <div className="bg-lime-400 text-black px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold inline-block mb-3 sm:mb-4">
                    COLLEGE YEARS
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Captain & Scholar</h3>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                    As Co-Captain at the Utah State University Tennis Team, a competitive excellence with academic pursuit combined 
                    for an incredible experience and a lifelong passion for learning and a discipline for hard work. Focusing studies 
                    in Human Movement Sciences deepened my understanding of how the body moves—which ultimately became invaluable 
                    in my lifelong pursuit of coaching in both tennis and pickleball.
                  </p>
                </div>
                
                {/* Timeline Icon */}
                <div className="absolute left-4 md:relative md:left-auto z-10 order-1 md:order-2 md:flex-shrink-0">
                  <div className="w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 bg-lime-400 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-black font-black text-xs sm:text-sm md:text-base">USU</span>
                  </div>
                </div>
                
                {/* Content Card - Right Side */}
                <div className="w-full md:w-1/2 order-3 pl-20 md:pl-0">
                  <div className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-2xl p-4 sm:p-6 md:p-8">
                    <div className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-4 text-center">🎓</div>
                    <p className="text-lime-400 font-semibold text-center text-sm sm:text-base">Utah State University</p>
                    <p className="text-gray-300 text-xs sm:text-sm text-center">USU Team Captain</p>
                  </div>
                </div>
              </div>

              {/* Timeline Item 4 - Pickleball Discovery */}
              <div className="relative flex flex-col md:flex-row-reverse items-start md:items-center gap-6 sm:gap-8">
                {/* Mobile/Desktop Content - Right Side */}
                <div className="w-full md:w-1/2 md:text-left order-2 md:order-1 pl-20 md:pl-0">
                  <div className="bg-lime-400 text-black px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold inline-block mb-3 sm:mb-4">
                    NEW PASSION
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Discovering Pickleball</h3>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                    After two decades of playing junior USTA tennis and a decade of coaching tennis, I found the sport of pickleball early in 2010. 
                    This dynamic sport captured my heart with its accessibility and infectious energy—embodying everything I love about teaching 
                    and connecting with students of all abilities. A new passion and love was born!
                  </p>
                </div>
                
                {/* Timeline Icon */}
                <div className="absolute left-4 md:relative md:left-auto z-10 order-1 md:order-2 md:flex-shrink-0">
                  <div className="w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 bg-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <Zap className="w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 text-white" />
                  </div>
                </div>
                
                {/* Content Card - Left Side */}
                <div className="w-full md:w-1/2 order-3 pl-20 md:pl-0">
                  <div className="bg-gradient-to-br from-purple-600/10 to-lime-400/10 border border-purple-600/20 rounded-2xl p-4 sm:p-6 md:p-8">
                    <div className="text-4xl sm:text-5xl md:text-6xl mb-2 sm:mb-4 text-center">🏓</div>
                    <p className="text-purple-400 font-semibold text-center text-sm sm:text-base">Pickleball Passion</p>
                    <p className="text-gray-300 text-xs sm:text-sm text-center">Accessibility meets infectious energy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PBX Purpose & Mission - Enhanced for About Page */}
      <section className="py-20 sm:py-24 bg-lime-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Header */}
          <div className="text-center mb-16 sm:mb-20">
            <div className="bg-black/10 rounded-2xl p-3 mb-6 sm:mb-8 inline-block">
              <span className="text-black font-bold text-sm sm:text-lg uppercase tracking-wide">🎯 OUR MISSION</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-black mb-6 sm:mb-8 uppercase tracking-tight leading-none">
              PBX PURPOSE
            </h2>
            <p className="text-xl sm:text-2xl text-black/80 max-w-4xl mx-auto leading-relaxed font-bold">
              Where Pickleball Meets Purpose. Style Serves a Greater Cause.
            </p>
            <p className="text-lg sm:text-xl text-black/70 max-w-3xl mx-auto leading-relaxed mt-4">
              We donate 10% of every adaptive gear sale to wheelchair sports programs. 
              Because true champions lift others up.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 items-start mb-16 sm:mb-20">
            {/* Left Side - Mission Details */}
            <div className="space-y-8">
              <div className="bg-black/90 rounded-3xl p-6 sm:p-8 border-2 border-black/30">
                <h3 className="text-2xl sm:text-3xl font-black text-lime-400 mb-6 uppercase tracking-tight">
                  Impact in Action
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-lime-400 rounded-full p-2 flex-shrink-0">
                      <Target className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-2">Adaptive Sports Programs</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Direct funding to wheelchair sports programs nationwide, providing equipment and training resources
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-lime-400 rounded-full p-2 flex-shrink-0">
                      <Users className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-2">Community Partnerships</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Collaborating with wheelchair sports organizations to expand accessibility and inclusion
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-lime-400 rounded-full p-2 flex-shrink-0">
                      <Heart className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-2">Athlete Visibility</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Championing adaptive athlete stories and changing perceptions about inclusive sport
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-lime-400 rounded-full p-2 flex-shrink-0">
                      <Award className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-2">Ambassador Program</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Supporting adaptive athletes through sponsorships and platform opportunities
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ADPT Collection Highlight */}
              <div className="bg-black/10 rounded-2xl p-6 sm:p-8 border-2 border-black/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-3xl">🔥</div>
                  <h4 className="font-black text-black text-xl sm:text-2xl">PBX ADPT COLLECTION</h4>
                </div>
                <p className="text-black/80 leading-relaxed mb-6">
                  Our specialized adaptive line designed in partnership with wheelchair athletes. 
                  Every purchase directly funds adaptive sports programs and equipment.
                </p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-black text-black">10%</div>
                    <div className="text-black/70 text-sm">OF SALES DONATED</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black text-black">$5000</div>
                    <div className="text-black/70 text-sm">FUNDS RAISED</div>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full bg-black text-lime-400 hover:bg-gray-900 font-black px-8 py-4 sm:py-5 rounded-2xl text-lg sm:text-xl transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                Learn More About Our Impact
                <ChevronRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
            </div>
            
            {/* Right Side - Testimonial & Visual */}
            <div className="space-y-8">
              {/* Main Testimonial */}
              <div className="bg-black/90 rounded-3xl p-6 sm:p-8 border-2 border-black/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-lime-400/20 to-purple-600/20"></div>
                <div className="relative z-10">
                  <div className="text-6xl sm:text-7xl mb-4 opacity-30">"</div>
                  <blockquote className="text-lime-400 text-xl sm:text-2xl font-bold mb-6 leading-relaxed -mt-8">
                    PBX was the first brand that made me feel like my adaptive needs weren't an afterthought—they were the inspiration.
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-lime-400 rounded-full flex items-center justify-center text-black font-bold text-lg">
                      J
                    </div>
                    <div>
                      <cite className="text-white font-bold not-italic">
                        Jordan M.
                      </cite>
                      <p className="text-lime-400 text-sm">Adaptive Pickleball Athlete & PBX Ambassador</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-lime-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Impact Statistics */}
              <div className="bg-black/10 rounded-2xl p-6 sm:p-8">
                <h4 className="font-black text-black text-xl sm:text-2xl mb-6 text-center">MEASURABLE IMPACT</h4>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-black text-black mb-2">500+</div>
                    <div className="text-black/70 text-sm font-medium">Athletes Supported</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-black text-black mb-2">25+</div>
                    <div className="text-black/70 text-sm font-medium">Programs Funded</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-black text-black mb-2">15</div>
                    <div className="text-black/70 text-sm font-medium">States Reached</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl sm:text-4xl font-black text-black mb-2">100%</div>
                    <div className="text-black/70 text-sm font-medium">Adaptive-First</div>
                  </div>
                </div>
              </div>

              {/* Philosophy Quote */}
              <div className="bg-gradient-to-br from-black/20 to-black/10 rounded-2xl p-6 sm:p-8 border-2 border-black/30 text-center">
                <div className="text-4xl mb-4">🏆</div>
                <p className="text-black font-bold text-lg sm:text-xl mb-4 leading-relaxed">
                  "True victory isn't just winning games—it's creating a world where everyone can play."
                </p>
                <p className="text-black/70 font-semibold">— Coach Felix, Founder</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-black/90 rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto border-2 border-black/30">
              <h3 className="text-3xl sm:text-4xl font-black text-lime-400 mb-6 uppercase tracking-tight">
                Join Our Mission
              </h3>
              <p className="text-xl text-white mb-8 leading-relaxed">
                Every purchase moves us closer to a more inclusive sporting world. 
                Shop our collection and be part of the change.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
                <Button 
                  className="bg-lime-400 text-black hover:bg-lime-300 font-black px-10 py-5 rounded-2xl text-xl transition-all duration-300 hover:shadow-2xl hover:shadow-lime-400/25 flex-1"
                >
                  SHOP ADPT COLLECTION
                  <ChevronRight className="ml-3 w-6 h-6" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-black px-10 py-5 rounded-2xl text-xl transition-all duration-300 flex-1"
                >
                  LEARN MORE
                  <ChevronRight className="ml-3 w-6 h-6" />
                </Button>
              </div>
              <p className="text-gray-400 text-sm mt-6">
                10% of every adaptive gear purchase supports wheelchair sports programs • #PlayBeyondExpectations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apparel Showcase Section */}
      <section className="py-20 bg-gradient-to-b from-lime-400 via-lime-500 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black mb-8 uppercase tracking-tight text-black drop-shadow-lg">
              MORE THAN APPAREL
            </h2>
            <p className="text-2xl text-black font-bold mb-6 drop-shadow-md">
              We're Leading a Movement
            </p>
            <p className="text-xl text-black/80 max-w-4xl mx-auto leading-relaxed drop-shadow-sm">
              PBX Pickle isn't just selling apparel—we're leading a revolution. Playing beyond expectations, 
              breaking down barriers, and ensuring that the joy of pickleball is truly accessible to everyone.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-black/90 to-gray-900/90 border-lime-400/30 hover:border-lime-400/60 transition-all duration-300 group backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <Target className="w-16 h-16 text-lime-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_0_8px_#C7FF00]" />
                <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide text-white">PREMIUM QUALITY</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Professional-grade materials and construction that rival the biggest athletic brands, 
                  with the added benefit of adaptive-first design philosophy.
                </p>
                <div className="mt-6 bg-lime-400/20 border border-lime-400/30 rounded-xl p-4">
                  <p className="text-lime-400 font-bold text-sm">
                    "Quality that competes with Nike, purpose that changes lives."
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-black/90 to-gray-900/90 border-purple-400/30 hover:border-purple-400/60 transition-all duration-300 group backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <Users className="w-16 h-16 text-purple-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_0_8px_#A855F7]" />
                <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide text-white">COMMUNITY DRIVEN</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Designed by adaptive athletes, for adaptive athletes. Every piece is tested and refined 
                  by our community of wheelchair players and inclusive sports advocates.
                </p>
                <div className="mt-6 bg-purple-400/20 border border-purple-400/30 rounded-xl p-4">
                  <p className="text-purple-400 font-bold text-sm">
                    "By the community, for the community."
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-black/90 to-gray-900/90 border-lime-400/30 hover:border-lime-400/60 transition-all duration-300 group backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <Heart className="w-16 h-16 text-lime-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_0_8px_#C7FF00]" />
                <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide text-white">SOCIAL IMPACT</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Every purchase creates ripple effects—funding wheelchair sports programs, 
                  supporting adaptive athlete development, and changing perceptions about inclusive sport.
                </p>
                <div className="mt-6 bg-lime-400/20 border border-lime-400/30 rounded-xl p-4">
                  <p className="text-lime-400 font-bold text-sm">
                    "Style that serves a purpose."
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-16">
            <div className="bg-gradient-to-br from-black/90 to-gray-900/90 border border-lime-400/30 rounded-3xl p-12 max-w-4xl mx-auto backdrop-blur-sm">
              <h3 className="text-3xl md:text-4xl font-black mb-6 text-lime-400 uppercase tracking-tight">
                Ready to Change the Game?
              </h3>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Shop the complete PBX Pickle collection now. 
                Be part of the movement that's redefining what adaptive sportswear can be.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
                <Button 
                  className="bg-lime-400 text-black hover:bg-lime-300 font-black px-10 py-5 rounded-2xl text-xl transition-all duration-300 hover:shadow-2xl hover:shadow-lime-400/25 flex-1"
                >
                  SHOP COLLECTION
                  <ChevronRight className="ml-3 w-7 h-7" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-black px-10 py-5 rounded-2xl text-xl transition-all duration-300 flex-1"
                >
                  VIEW BESTSELLERS
                  <ChevronRight className="ml-3 w-7 h-7" />
                </Button>
              </div>
              <p className="text-gray-400 text-sm mt-6">
                Free shipping on orders $75+ • 30-day satisfaction guarantee • Adaptive athlete approved
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Philosophy */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black mb-6 uppercase tracking-tight" style={metallicTextStyle}>
              Your Coaching Journey Starts Here
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Whether you're picking up a paddle for the first time or aiming to refine your advanced techniques, 
              my goal is to make your time on the court both rewarding and incredibly fun.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <Card className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-lime-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-bold mb-4 uppercase tracking-wide" style={metallicTextStyle}>Comprehensive Approach</h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  Focusing on everything from footwork and grip to strategy and mental game. 
                  Every lesson is tailored to your individual goals and abilities.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-lime-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-bold mb-4 uppercase tracking-wide" style={metallicTextStyle}>Adaptive Specialty</h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  Specialized techniques for players of all abilities, including wheelchair athletes and adaptive sports. 
                  Your unique needs drive our coaching approach.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <Heart className="w-12 h-12 text-lime-400 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-bold mb-4 uppercase tracking-wide" style={metallicTextStyle}>Community Building</h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  Successfully managed outdoor programs for 200+ students, creating inclusive environments 
                  where every player feels welcome and supported.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <div className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-2xl p-12 max-w-4xl mx-auto">
              <blockquote className="text-3xl md:text-4xl font-bold mb-6 leading-relaxed" style={metallicTextStyle}>
                "My coaching approach ensures that your time with PBX Pickle is truly worth your while."
              </blockquote>
              <p className="text-lime-400 text-xl font-semibold">— Coach Felix</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-lime-400 text-black hover:bg-lime-300 font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:shadow-xl"
                >
                  Book a Session
                  <Calendar className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300"
                >
                  Contact Coach Felix
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black mb-6 uppercase tracking-tight" style={metallicTextStyle}>
              Have Questions?
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Let's talk pickleball! Here are answers to the most common questions about coaching, 
              our mission, and what you can expect from PBX Pickle.
            </p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border border-lime-400/20 rounded-xl overflow-hidden transition-all duration-300 hover:border-lime-400/40"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-lime-400/5 transition-colors duration-300"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  <div className="flex-shrink-0">
                    {openFaq === index ? (
                      <Minus className="w-6 h-6 text-lime-400" />
                    ) : (
                      <Plus className="w-6 h-6 text-lime-400" />
                    )}
                  </div>
                </button>
                
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <div className="pt-4 border-t border-lime-400/10">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-lg text-gray-300 mb-8">
              Still have questions? I'm always here to help you get the most out of your pickleball journey.
            </p>
            <Button 
              className="bg-lime-400 text-black hover:bg-lime-300 font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:shadow-xl"
            >
              Contact Coach Felix Directly
              <ChevronRight className="ml-2 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-t from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 uppercase tracking-tight" style={metallicTextStyle}>
            Ready to Play Beyond Xpectations?
          </h2>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed">
            Join the PBX Pickle movement where passion meets purpose, and every player finds their place on the court.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Button 
              className="bg-lime-400 text-black hover:bg-lime-300 font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:shadow-xl w-full"
            >
              Start Coaching
              <ChevronRight className="ml-2 w-6 h-6" />
            </Button>
            <Button 
              variant="outline"
              className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 w-full"
            >
              Shop the Collection
              <ChevronRight className="ml-2 w-6 h-6" />
            </Button>
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl text-lime-400 font-bold mb-2">
              "Because the court should feel like home—for all of us."
            </p>
            <p className="text-gray-400">— Coach Felix, Founder & Head of Adaptive Design</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
